import Actions from 'actions'
import { API, graphqlOperation } from 'aws-amplify'
import { ofType } from 'redux-observable'
import i18n from 'resources/locales/i18n'
import { EMPTY, concat, from, of } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, pluck, switchMap } from 'rxjs/operators'
import { logError } from 'utils/logUtils'
import { mutateEntity } from 'utils/graphqlUtils'

export const fetchTrackingRequiredGrinScansEpic = (action$, state$) =>
  action$.pipe(
    ofType(Actions.HI_NOTIFY_PRACTICE),
    pluck('payload'),
    switchMap(({ patientId, reason, note, selectedPracticeMembersIds }) =>
      from(
        API.post('grinServerlessApi', '/treatments/v2/gi/notifyPractice', {
          body: {
            patientId,
            reason,
            note,
            selectedPracticeMembersIds
          }
        })
      ).pipe(
        mergeMap(() =>
          concat(
            of(Actions.hiNotifyPracticeReceived()),
            of(
              Actions.showSnackbar({
                text: i18n.t('messages.notifyPractice.submittedSuccessfully'),
                type: 'success'
              })
            )
          )
        ),
        catchError(
          error => concat(of(Actions.hiNotifyPracticeFailed(error))),
          of(
            Actions.showSnackbar({
              text: i18n.t('messages.notifyPractice.failedToNotify'),
              type: 'error'
            })
          )
        )
      )
    )
  )

/**
 * Keeps the patient's needsAttention tag in step with an edited Needs Attention note (GR-10746).
 * The Copilot tab and the header read the text from the tag's metadata.note, not from the note.
 * Tag and note share no id, so NoteItem sends the tag and patient it saw before saving, and the tag is
 * re-read here: it is written only if it still exists, belongs to that patient and still holds the
 * note's previous text. The write merges into that fresh metadata, so a newer re-notify is not undone.
 */
const fetchPatientTag = async id => {
  const response = await API.graphql(
    graphqlOperation(
      `query ($id: ID!) {
        getPatientTag(id: $id) {
          id
          metadata
          patientTagPatientId
          _deleted
        }
      }`,
      { id }
    )
  )
  return response.data?.getPatientTag
}

export const updateNeedsAttentionTagNoteEpic = action$ =>
  action$.pipe(
    ofType(Actions.UPDATE_NEEDS_ATTENTION_TAG_NOTE),
    pluck('payload'),
    filter(({ patientTagId, previousNote = '', note = '' }) => !!patientTagId && previousNote.trim() !== note.trim()),
    // concatMap: a second quick edit must not cancel the first one's result.
    concatMap(({ patientId, patientTagId, previousNote = '', note = '' }) =>
      from(fetchPatientTag(patientTagId)).pipe(
        mergeMap(patientTag => {
          const metadata = JSON.parse(patientTag?.metadata || '{}')
          const isSameRequest =
            !!patientTag &&
            !patientTag._deleted &&
            patientTag.patientTagPatientId === patientId &&
            (metadata.note || '').trim() === previousNote.trim()

          if (!isSameRequest) {
            return EMPTY
          }

          return from(
            mutateEntity({
              id: patientTag.id,
              entityType: 'PatientTag',
              input: { metadata: JSON.stringify({ ...metadata, note }) },
              fields: ['id', '_version', 'metadata', 'patientTagPatientId']
            })
          ).pipe(map(updatedFields => Actions.updateNeedsAttentionTagNoteReceived(updatedFields)))
        }),
        catchError(error => {
          logError('Failed to update the needsAttention tag note', { patientTagId, error })
          return concat(
            of(Actions.updateNeedsAttentionTagNoteFailed(error)),
            of(
              Actions.showSnackbar({
                text: i18n.t('pages.patients.patientSwitchableView.notes.updateNeedsAttentionTagError'),
                type: 'error'
              })
            )
          )
        })
      )
    )
  )
